import { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import LSCommon from './components/LSCommon';
import LocalSEOAI from './components/LocalSEOAI';
import GBPConnectionGuard from './components/GBPConnectionGuard';
import { useGetLocalSEOReport } from './hooks/local-seo-apis';
import { RootState } from 'src/redux/store';
import { useSelector } from 'react-redux';
import { useGetAgentsStatus } from '../hooks/useGetAgentsStatus';
import { useOrbitAISSE } from '../hooks/useOrbitAISSE';
import OrbitAIGuard from '../Components/OrbitAIGuard';
import UpgradeToOrbitAI from '../Components/UpgradeToOrbitAI';
import LocalSeoAiBreadcrumbs from './components/LocalSeoAiBreadcrumbs';
import LocalSeoAiHeader from './components/LocalSeoAiHeader';
import { Card } from 'src/components/UI/Card';
import useSiteGBP1to1Mapping, { IGBPLocation } from '../hooks/useSiteGBP1to1Maping';

import { otherToolMenuHeadings, paths } from 'src/common/config/TextMessage';
import OrbitAgentSiteGuard from '../Components/OrbitAgentSiteGuard';

function LocalSeoAiFallback() {
  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div className="flex justify-between items-center mb-3">
        <LocalSeoAiBreadcrumbs />
        <LocalSeoAiHeader />
      </div>
      <Card>
        <div className="px-6">
          <p className="text-primary font-semibold text-3xl">Local SEO AI</p>
          <div className="w-[600px] mx-auto my-10">
            <UpgradeToOrbitAI />
          </div>
        </div>
      </Card>
    </div>
  );
}

function LocalSeoAiContent() {
  const { _id: siteId, user_id: userId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};

  useOrbitAISSE(siteId);

  const [manuallySelectedProfile, setManuallySelectedProfile] = useState<IGBPLocation | null>(null);

  const siteGBP1to1Configurations = useSiteGBP1to1Mapping({ siteId });

  const selectedProfile = useMemo(() => {
    if (manuallySelectedProfile) return manuallySelectedProfile;
    if (!siteGBP1to1Configurations || siteGBP1to1Configurations.isConfigurationError) return null;
    return siteGBP1to1Configurations.connectedGBPAccount ?? null;
  }, [manuallySelectedProfile, siteGBP1to1Configurations]);

  const {
    data: localSEOReport,
    isLoading: isLocalSEOReportLoading,
    isPending: isLocalSEOReportPending,
    refetch: refetchLocalSeo
  } = useGetLocalSEOReport({
    userId: userId,
    siteId: siteId,
    accountId: selectedProfile?.accountId ? String(selectedProfile.accountId) : '',
    locationId: selectedProfile?.name ? (selectedProfile.name.includes('/') ? selectedProfile.name.split('/')[1] : selectedProfile.name) : String(selectedProfile?.locationId || '')
  });

  const { data: agentsStatus } = useGetAgentsStatus(siteId);

  const isLocalSEOAgentProcessing = agentsStatus?.agents?.['local-seo']?.status === 'processing';

  useEffect(() => {
    if (!isLocalSEOAgentProcessing && agentsStatus) {
      refetchLocalSeo();
    }
  }, [isLocalSEOAgentProcessing, agentsStatus, refetchLocalSeo]);

  const hasReport = useMemo(() => {
    if (isLocalSEOAgentProcessing) return false;
    return Boolean(localSEOReport && localSEOReport.hasReport);
  }, [localSEOReport, isLocalSEOAgentProcessing]);

  const handleGBPProfileSelection = (profile: IGBPLocation) => {
    if (!profile) return;
    setManuallySelectedProfile(profile);
  };

  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div aria-label="local-seo-ai-common-header-and-progress">
        <LSCommon
          onSelect={handleGBPProfileSelection}
          selectedProfile={selectedProfile}
          hasReport={hasReport}
        />
      </div>

      <div>
        <GBPConnectionGuard>
          <LocalSEOAI 
            hasReport={hasReport} 
            data={localSEOReport} 
            isLoading={isLocalSEOReportLoading || isLocalSEOAgentProcessing} 
            isPending={isLocalSEOReportPending} 
            selectedProfile={selectedProfile}
          />
        </GBPConnectionGuard>
      </div>
    </div>
  );
}

function LocalSeoAiPage() {
  return (
    <>
      <Helmet>
        <title>SEOSpace | Local SEO AI</title>
      </Helmet>

      <OrbitAIGuard fallback={<LocalSeoAiFallback />}>
        <OrbitAgentSiteGuard
          title={otherToolMenuHeadings.localSeoAi}
          parentTitle="Local SEO"
          parentPath={paths.otherTools}
          headerComponent={<LocalSeoAiHeader />}
        >
          <LocalSeoAiContent />
        </OrbitAgentSiteGuard>
      </OrbitAIGuard>
    </>
  );
}

export default LocalSeoAiPage;
